import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import LoopIcon from '@mui/icons-material/Loop';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import './RecurrentEventModal.scss';
import moment from 'moment';
import CircleDays from './CircleDays';
import {
  getEventDatesByMonth,
  getEventDatesByYear,
  getEventDatesByWeek,
  getEventDatesByDays,
} from './parserRecurrentDates';
import ActiveDaysMessage from './ActiveDaysMessage';
import { onDisplayAlert } from '../../utils/alerts/alerts';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'white',
  border: '0px solid #000',
  boxShadow: 24,
  borderRadius: 1,
  p: 5,
  '@media (max-width: 600px)': {
    width: 300,
    overflowY: 'auto',
    maxHeight: 550,
  },
  '@media (max-height: 500px)': {
    width: 300,
    overflowY: 'scroll',
    maxHeight: 380,
  },
};

function RecurrentEventModal({
  isOpen,
  onSave,
  handleClose,
  repeatEvery,
  onChangeRepeatEvery,
  repeatEveryList,
  startEventDate,
  setStartEventDate,
}) {
  const [repeatEveryCount, setRepeatEveryCount] = useState(1);
  const [endRecurrenceEventDate, setEndRecurrenceEventDate] = useState(moment().add(1, 'day'));

  const [weekDays, setWeekDays] = useState([
    { id: 7, name: 'Sunday', initialCharacter: 'S', date: null, active: false },
    { id: 1, name: 'Monday', initialCharacter: 'M', date: null, active: true },
    { id: 2, name: 'Tuesday', initialCharacter: 'T', date: null, active: false },
    { id: 3, name: 'Wednesday', initialCharacter: 'W', date: null, active: false },
    { id: 4, name: 'Thursday', initialCharacter: 'T', date: null, active: false },
    { id: 5, name: 'Friday', initialCharacter: 'F', date: null, active: false },
    { id: 6, name: 'Saturday', initialCharacter: 'S', date: null, active: false },
  ]);

  const showCircleDays =
    repeatEvery === 'week' || (repeatEvery === 'day' && repeatEveryCount === 1);

  const getRepeatList = () => repeatEveryList?.slice(1, repeatEveryList.length);

  const onActiveDay = (day) => {
    let count = 0;
    weekDays.forEach((i) => {
      if (i.active) count += 1;
    });

    if (count === 1 && day.active) return;
    setWeekDays(
      weekDays.map((item) => (item.id === day.id ? { ...day, active: !day.active } : item)),
    );
  };

  const getRepeatEveryCount = () => {
    let i = 1;
    // eslint-disable-next-line no-plusplus
    return Array.from({ length: 100 }, () => i++);
  };

  function validationDateAlert() {
    onDisplayAlert({
      type: 'info',
      title: 'Check Dates',
      html: (
        <p>
          Verify that the start date of your event is not equal to or greater than the end date of
          the recurring event.
        </p>
      ),
    });
  }

  const handlerSaveData = () => {
    if (moment(startEventDate) >= moment(endRecurrenceEventDate)) validationDateAlert();
    let eventRecurrentList = [];
    switch (repeatEvery) {
      case 'year':
        eventRecurrentList = getEventDatesByYear(startEventDate, endRecurrenceEventDate);
        break;
      case 'month':
        eventRecurrentList = getEventDatesByMonth(
          startEventDate,
          endRecurrenceEventDate,
          repeatEveryCount,
        );
        break;
      case 'week': {
        const daysActives = weekDays.filter((item) => item.active).map((i) => i.id);
        eventRecurrentList = getEventDatesByWeek(
          startEventDate.toString(),
          endRecurrenceEventDate.toString(),
          repeatEveryCount,
          daysActives,
        );
        break;
      }
      case 'day': {
        const daysActives = weekDays.filter((item) => item.active).map((i) => i.id);
        eventRecurrentList = getEventDatesByDays(
          startEventDate.toString(),
          endRecurrenceEventDate.toString(),
          repeatEveryCount,
          daysActives,
        );
        break;
      }
      default:
        break;
    }

    onSave({
      Hints: (
        <ActiveDaysMessage
          weekDays={weekDays}
          repeatEvery={repeatEvery}
          repeatEveryCount={repeatEveryCount}
          startEventDate={startEventDate}
          endRecurrenceEventDate={endRecurrenceEventDate}
        />
      ),
      repeatEveryCount,
      eventRecurrentList,
      endRecurrence: endRecurrenceEventDate.format('YYYY-MM-DD'),
    });
  };

  function onChangeEventDate(date) {
    setStartEventDate('event_date', moment(date.toString()));
    const weekDayNumber = date.isoWeekday();
    setWeekDays(
      weekDays.map((day) =>
        day.id === weekDayNumber ? { ...day, active: true } : { ...day, active: false },
      ),
    );
  }

  useEffect(() => {
    if (repeatEvery === 'day') {
      setWeekDays(weekDays.map((day) => ({ ...day, active: true })));
    } else if (repeatEvery === 'week') {
      const weekDay = startEventDate.isoWeekday();
      setWeekDays(
        weekDays.map((day) =>
          day.id === weekDay ? { ...day, active: true } : { ...day, active: false },
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatEvery]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={style}>
        <Typography id="modal-modal-title" variant="h5" component="h5">
          Repeat
        </Typography>
        <Grid item justifyContent="center" marginTop={3}>
          <Grid item sm={10}>
            <Typography>Start event</Typography>
            <FormControl sx={{ m: 1, minWidth: 80 }} size="small">
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  name="event_date"
                  minDate={moment()}
                  displayStaticWrapperAs="desktop"
                  inputVariant="standard"
                  value={startEventDate}
                  onChange={(value) => onChangeEventDate(value)}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Grid item sm={10}>
            <Typography>Repeat every</Typography>
            <Grid container>
              <Grid item sm={1} marginTop={1.5}>
                <LoopIcon style={{ fontSize: 35 }} />
              </Grid>
              {repeatEvery !== 'year' && (
                <Grid item sm={3}>
                  <FormControl sx={{ m: 1, minWidth: 80 }} size="small">
                    <Select
                      value={repeatEveryCount}
                      onChange={(e) => setRepeatEveryCount(e.target.value)}>
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {getRepeatEveryCount().map((i) => (
                        <MenuItem key={i} value={i}>
                          {i}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item sm={5}>
                <FormControl sx={{ m: 1, minWidth: 80 }} size="small">
                  <Select
                    labelId="demo-simple-select-autowidth-label"
                    id="demo-simple-select-autowidth"
                    value={repeatEvery}
                    onChange={(e) => onChangeRepeatEvery(e.target.value)}
                    autoWidth>
                    {getRepeatList().map((i) => (
                      <MenuItem key={i.id} value={i.id}>
                        {i.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {showCircleDays && (
              <Grid container justifyContent="center">
                <Grid item sm={11} lg={12} md={12} marginLeft={2} marginTop={2}>
                  <CircleDays weekDays={weekDays} onActiveDay={onActiveDay} />
                </Grid>
              </Grid>
            )}

            <Grid item sm={11} lg={12} md={12} marginLeft={2} marginTop={2} marginBottom={2}>
              <Typography variant="body">
                <ActiveDaysMessage
                  weekDays={weekDays}
                  repeatEvery={repeatEvery}
                  repeatEveryCount={repeatEveryCount}
                  startEventDate={startEventDate}
                  endRecurrenceEventDate={endRecurrenceEventDate}
                />
              </Typography>
            </Grid>

            <Grid container justifyContent="center">
              <Grid item sm={11}>
                <FormControl sx={{ m: 1, minWidth: 80 }} size="small">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      displayStaticWrapperAs="desktop"
                      inputVariant="standard"
                      minDate={moment().add(1, 'day')}
                      value={endRecurrenceEventDate}
                      onChange={(value) => setEndRecurrenceEventDate(value)}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container justifyContent="space-evenly" marginTop={3}>
              <Grid>
                <Button
                  onClick={() => handleClose()}
                  size="large"
                  variant="outlined"
                  color="inherit"
                  style={{ borderRadius: 20 }}>
                  Discard
                </Button>
              </Grid>
              <Grid>
                <Button
                  onClick={() => handlerSaveData()}
                  size="large"
                  variant="contained"
                  color="primary"
                  style={{ borderRadius: 20 }}>
                  Save
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

RecurrentEventModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  repeatEvery: PropTypes.any.isRequired,
  onChangeRepeatEvery: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  repeatEveryList: PropTypes.any.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  startEventDate: PropTypes.any.isRequired,
  setStartEventDate: PropTypes.func.isRequired,
};

export default RecurrentEventModal;
